import React from 'react';
import type { FormikValues } from 'formik';
import { withFormik } from 'formik';
import { object, ref, string } from 'yup';

import { get } from 'src/helper/helper';
import HttpService from 'src/services/api/http';
import type { AfterLoginCallback } from 'src/components/app/context/LoginConsumer';
import LoginConsumer from 'src/components/app/context/LoginConsumer';

import PasswordResetConfirmForm from '../PasswordResetConfirmForm/index.js';

// Regular expression that must contain at least 8 characters, a capital letter, a non-capital letter and a number.
const passwordRegex =
  /^(?=(.*?[A-ZÄÖÜ]){1,})(?=(.*[a-zäöüß]){1,})(?=(.*\d){1,})(?!.*\s).{8,}$/;

const validationSchema = object().shape({
  password1: string()
    .required('login:passwordOneRequired')
    .matches(passwordRegex, 'login:passwordRegexError'),
  password2: string()
    .oneOf([ref('password1'), null], 'login:passwordDoNotMatchError')
    .required('login:passwordTwoRequired'),
});

/**
 * This function transfers error messages we receive from the api to the format of formik. Formik's error property
 * simply holds an object with keys equal to the names of the fields, e.g. {email: 'wrong', password:'big error'}.
 */
export const transformApiErrors = (data: {
  non_field_errors: { nonFieldErrors: any };
  uid: string;
  token: string;
  new_password1: string;
  new_password2: string;
}) => {
  if (!data) return {};

  const { non_field_errors: nonFieldErrors, uid, token } = data;
  let generalErrors;
  if (uid || token) {
    generalErrors =
      'The link to reset the password is not valid. Please request a new email.';
  } else {
    generalErrors = nonFieldErrors;
  }

  return {
    ...data,
    password1: get(() => data.new_password1),
    password2: get(() => data.new_password2),
    nonFieldErrors: generalErrors,
  };
};

export const handleSubmit = (
  { password1, password2 }: { password1: string; password2: string },
  { setSubmitting, setErrors, props }: FormikValues,
) => {
  setSubmitting(true);
  const payload = {
    new_password1: password1,
    new_password2: password2,
    uid: props.uid,
    token: props.token,
  };
  return HttpService.post('account/password/reset/confirm/', payload)
    .then((response) => {
      setSubmitting(false);
      // Sets auth token in session storage, loads userContext, triggers provided onDone()
      props.afterLogin(response.data.key, props.onDone);
      return true;
    })
    .catch((error) => {
      setSubmitting(false);
      setErrors(transformApiErrors(get(() => error.response.data)));
      return false;
    });
};

const PasswordResetConfirmFormContainer = withFormik({
  mapPropsToValues: () => ({ password1: '', password2: '' }),
  validationSchema,
  handleSubmit,
})(PasswordResetConfirmForm);

const PasswordResetConfirmFormContainerContextProvider = (props: any) => (
  <LoginConsumer
    render={(afterLogin: AfterLoginCallback) => (
      <PasswordResetConfirmFormContainer {...props} afterLogin={afterLogin} />
    )}
  />
);

export default PasswordResetConfirmFormContainerContextProvider;
